import orderBy from "lodash/orderBy";

/** Optional axis / value bullet label text colors (amCharts fill hex). */
export type TARSChartLabelColors = {
  category?: string;
  value?: string;
};

/** Chart presentation options stored on facet chart JSON `configuration`. */
export type TARSChartConfiguration = {
  /** Fill color per row/category label (same string as first column). */
  colorByLabel?: Record<string, string>;
  labelColors?: TARSChartLabelColors;
  /**
   * Appended after formatted values in bar/column data labels and tooltips (e.g. `"%"`).
   * Omit or empty string disables unless the value-column header suggests a percent (see resolver).
   */
  valueSuffix?: string;
  titleFontSize?: number;
  axisFontSize?: number;
  dataLabelFontSize?: number;
};

/** New / persisted chart JSON: color-related keys present; no hex values yet. */
export function emptyChartConfiguration(): TARSChartConfiguration {
  return {
    colorByLabel: {},
    labelColors: {
      category: "",
      value: "",
    },
  };
}

export type TARSBarChartTableData = {
  rows: unknown[][];
  headers: unknown[];
  /** When true, category order follows `rows` (e.g. facet ETL `row_sort`). */
  preserveRowOrder?: boolean;
  /** Optional; shown centered above the chart in bold. */
  title?: string;
  /** Optional parallel array of #hex colors, one per row (same order as `rows`). */
  colors?: unknown[];
  /** Facet chart `configuration` (label colors, color-by-label map, etc.). */
  configuration?: TARSChartConfiguration;
};

/** One bar: category label (X), numeric value (Y), optional user fill color. */
export type TARSBarChartPoint = { label: string; value: number; fillHex?: string };

export function parseOptionalColorHex(cellValue: unknown): string | undefined {
  const raw = String(cellValue ?? "").trim();
  if (!raw) return undefined;
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(raw)) return raw;
  if (/^([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(raw)) return `#${raw}`;
  return undefined;
}

/** Normalize user/config color input to `#rrggbb` when possible. */
export function normalizeColorInput(color: string): string {
  const parsed = parseOptionalColorHex(color);
  return parsed ?? color.trim();
}

export const DEFAULT_CATEGORY_LABEL_HEX = "#111827";
export const DEFAULT_VALUE_LABEL_HEX = "#111827";

/** True when `configuration.colorByLabel` is a usable lookup object (not array/null). */
export function isColorByLabelMap(
  value: unknown
): value is Record<string, string> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/** Resolve fill from `configuration.colorByLabel` using row/segment label string. */
export function resolveHexFromColorByLabel(
  map: Record<string, string> | unknown | undefined,
  key: string
): string | undefined {
  if (!isColorByLabelMap(map)) return undefined;
  const raw = map[key.trim()] ?? map[key];
  return parseOptionalColorHex(raw);
}

function headerSuggestsPercent(header: string): boolean {
  if (!header) return false;
  if (header.includes("%")) return true;
  if (/\bpercent(age)?\b/i.test(header)) return true;
  if (/\bpct\b/i.test(header)) return true;
  if (/\bshare\b/i.test(header)) return true;
  return false;
}

/**
 * When headers omit "%", infer from values: 0–100 with decimals (e.g. 62.3), or integers
 * summing to ~100 (vote/poll shares). Skips cases like two scores 80+90 (sum > 102).
 */
export function inferPercentSuffixFromValues(values: number[]): boolean {
  if (values.length === 0) return false;
  if (!values.every((v) => Number.isFinite(v))) return false;
  if (!values.every((v) => v >= 0 && v <= 100)) return false;

  const sum = values.reduce((a, b) => a + b, 0);
  const anyFraction = values.some((v) => Math.abs(v - Math.round(v)) > 1e-6);
  if (anyFraction) return true;

  return sum >= 98 && sum <= 102;
}

/**
 * Suffix for bar/column value labels (e.g. `%`). Non-empty `configuration.valueSuffix` wins;
 * explicit `""` disables; else header text or inferred values (see `inferPercentSuffixFromValues`).
 */
export function resolveTarsBarColumnValueSuffix(
  headers: unknown[],
  configuration?: TARSChartConfiguration,
  valueColumnSamples?: number[]
): string {
  const fromConfig = configuration?.valueSuffix;
  if (typeof fromConfig === "string") {
    if (fromConfig !== "") return fromConfig;
    return "";
  }
  const h1 = headers.length > 1 ? String(headers[1] ?? "").trim() : "";
  if (headerSuggestsPercent(h1)) {
    return "%";
  }
  if (valueColumnSamples && inferPercentSuffixFromValues(valueColumnSamples)) {
    return "%";
  }
  return "";
}

type TarsBarColumnValueField = "valueX" | "valueY";

/**
 * Bar/column bullet text: number bold; suffix (e.g. `%`) after `[/]` so amCharts text styling
 * does not treat `%` inside `[bold]…[/]` as formatting and drop the value.
 */
export function tarsBarColumnValueBulletText(
  valueField: TarsBarColumnValueField,
  valueSuffix: string
): string {
  const n = `{${valueField}.formatNumber('#.##')}`;
  if (valueSuffix === "") return `[bold]${n}[/]`;
  return `[bold]${n}[/]${valueSuffix}`;
}

/** Tooltip line for a bar/column series (category token must be `{categoryX}` or `{categoryY}`). */
export function tarsBarColumnValueTooltipText(
  categoryToken: "{categoryX}" | "{categoryY}",
  valueField: TarsBarColumnValueField,
  valueSuffix: string
): string {
  const n = `{${valueField}.formatNumber('#.##')}`;
  const valuePart =
    valueSuffix === "" ? `[bold]${n}[/]` : `[bold]${n}[/]${valueSuffix}`;
  return `${categoryToken}: ${valuePart}`;
}

export function resolveLabelFillHex(
  labelColors: TARSChartLabelColors | undefined | unknown,
  role: "category" | "value",
  /** When `labelColors` has no valid hex for this role, use this instead of the global default. */
  fallbackHex?: string
): string {
  const lc =
    labelColors != null &&
    typeof labelColors === "object" &&
    !Array.isArray(labelColors)
      ? (labelColors as TARSChartLabelColors)
      : undefined;
  const raw = role === "category" ? lc?.category : lc?.value;
  const fromConfig = parseOptionalColorHex(raw);
  if (fromConfig) return fromConfig;
  const fromFallback = fallbackHex ? parseOptionalColorHex(fallbackHex) : undefined;
  if (fromFallback) return fromFallback;
  return role === "category" ? DEFAULT_CATEGORY_LABEL_HEX : DEFAULT_VALUE_LABEL_HEX;
}

/** Parses a table cell into a finite number, or null if not numeric. */
export function parseNumericCellValue(cellValue: unknown): number | null {
  if (typeof cellValue === "number" && Number.isFinite(cellValue)) return cellValue;

  const normalizedString = String(cellValue ?? "")
    .trim()
    .replace(/,/g, "");
  if (normalizedString === "") return null;

  const parsedNumber = Number(normalizedString);
  return Number.isFinite(parsedNumber) ? parsedNumber : null;
}

function resolveColorColumnIndex(columnHeaders: unknown[]): number | null {
  for (let i = 0; i < columnHeaders.length; i++) {
    if (String(columnHeaders[i]).toLowerCase() === "color") return i;
  }
  return null;
}

/**
 * Builds bar series from facet-style table JSON: column 0 = label, column 1 = value.
 * Optional color: header named `color`, or third column when there are ≥3 headers, or `colors[i]`.
 */
export function rowsToTARSBarSeries(data: TARSBarChartTableData): TARSBarChartPoint[] {
  const columnHeaders = Array.isArray(data.headers) ? data.headers : [];
  const tableRows = Array.isArray(data.rows) ? data.rows : [];
  const parallelColors = Array.isArray(data.colors) ? data.colors : null;
  if (columnHeaders.length < 2 || tableRows.length === 0) return [];

  const colorColumnIndex = resolveColorColumnIndex(columnHeaders);
  const colorByLabelRaw = data.configuration?.colorByLabel;
  const colorByLabel = isColorByLabelMap(colorByLabelRaw) ? colorByLabelRaw : undefined;
  const barSeriesPoints: TARSBarChartPoint[] = [];

  tableRows.forEach((rowCells, rowIndex) => {
    if (!Array.isArray(rowCells) || rowCells.length < 2) return;

    const categoryCellRaw = rowCells[0];
    const numericValue = parseNumericCellValue(rowCells[1]);
    if (numericValue == null) return;

    const label =
      categoryCellRaw != null && String(categoryCellRaw).trim() !== ""
        ? String(categoryCellRaw)
        : "—";

    let fillHex: string | undefined = resolveHexFromColorByLabel(colorByLabel, label);
    if (colorColumnIndex != null && rowCells.length > colorColumnIndex) {
      fillHex = fillHex ?? parseOptionalColorHex(rowCells[colorColumnIndex]);
    }
    if (fillHex == null && parallelColors && parallelColors[rowIndex] != null) {
      fillHex = parseOptionalColorHex(parallelColors[rowIndex]);
    }

    const point: TARSBarChartPoint = { label, value: numericValue };
    if (fillHex) point.fillHex = fillHex;
    barSeriesPoints.push(point);
  });

  return barSeriesPoints;
}

/** Default: sort by value desc; with `preserveRowOrder`, keep table row sequence. */
export function orderBarChartPoints(
  points: TARSBarChartPoint[],
  preserveRowOrder?: boolean
): TARSBarChartPoint[] {
  if (preserveRowOrder) return points;
  return orderBy(points, "value", "desc");
}
